import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Package, 
  Save, 
  ArrowLeft,
  Camera,
  FileDown,
  Thermometer,
  Scale,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Check,
  X,
  ChefHat,
  User
} from 'lucide-react';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

// Numeric input component - defined outside to prevent re-creation on each render
const NumericInput = ({ label, value, onChange, placeholder, unit, className = '', required }) => (
  <div className={className}>
    <Label className="text-sm text-slate-600">{label}</Label>
    <div className="relative mt-1">
      <Input
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(',', '.'))}
        placeholder={placeholder}
        className="h-14 text-lg pr-12 touch-manipulation"
        required={required}
      />
      {unit && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">{unit}</span>
      )}
    </div>
  </div>
);

const BalirnaDetailPage = () => {
  const { id, productionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productionRecord, setProductionRecord] = useState(null);
  const [kitchenHandover, setKitchenHandover] = useState(null);
  const [isNew, setIsNew] = useState(!id);
  
  // Production completion modal state - REMOVED, now inline in form
  const [completionData, setCompletionData] = useState({
    shortage_reasons: [], // Array for multiple selections: ['omacka', 'maso', etc.]
    shortage_reason_other: '',
    shortage_comment: ''
  });
  
  // Form state
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    packaging_start_time: '',
    packaging_end_time: '',
    product_name: '',
    batch_number: '',
    product_temperature: '',
    packaged_quantity: '',
    packaging_weight: '',
    declared_weight: '',
    weight_checks: ['', '', '', '', '', '', '', '', '', ''],
    packaging_correct: null,
    seam_correct: null,
    first_product_ok: null,
    last_product_ok: null,
    allergen_sanitation: null,
    passes_metal_detector: null,
    // Metal detector - start of batch
    etalon_stainless_start: null,
    etalon_iron_start: null,
    etalon_nonferrous_start: null,
    // Metal detector - end of batch
    etalon_stainless_end: null,
    etalon_iron_end: null,
    etalon_nonferrous_end: null,
    filled_by: '',
    ink_jet_photo: null
  });

  useEffect(() => {
    if (id) {
      fetchExistingRecord();
    } else if (productionId) {
      fetchProductionRecord();
    }
  }, [id, productionId]);

  const fetchExistingRecord = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/balirna/records/${id}`);
      const record = response.data;
      
      setFormData({
        ...record,
        weight_checks: record.weight_checks || ['', '', '', '', '', '', '', '', '', '']
      });
      setProductionRecord(record.production_data);
      setIsNew(false);
      
      // Load completion data (shortage reasons and comment)
      if (record.completion_data) {
        setCompletionData({
          shortage_reasons: record.completion_data.shortage_reasons || [],
          shortage_reason_other: record.completion_data.shortage_reason_other || '',
          shortage_comment: record.completion_data.shortage_comment || ''
        });
      }
      
      // Also fetch kitchen handover if we have production_record_id
      if (record.production_record_id) {
        try {
          const handoverResponse = await api.get(`/kitchen/handover/${record.production_record_id}`);
          setKitchenHandover(handoverResponse.data.handover);
        } catch (e) {
          console.log('No kitchen handover found');
        }
      }
    } catch (error) {
      console.error('Error fetching record:', error);
      toast.error('Chyba při načítání záznamu');
      navigate('/balirna');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductionRecord = async () => {
    try {
      setLoading(true);
      const [prodResponse, handoverResponse] = await Promise.all([
        api.get(`/production/records/${productionId}`),
        api.get(`/kitchen/handover/${productionId}`)
      ]);
      const record = prodResponse.data;
      
      setProductionRecord(record);
      setKitchenHandover(handoverResponse.data.handover);
      setFormData(prev => ({
        ...prev,
        product_name: record.data?.Popis || record.data?.Výrobek || '',
        batch_number: record.data?.Šarže || record.data?.['Číslo šarže'] || '',
        filled_by: user?.name || ''
      }));
      setIsNew(true);
    } catch (error) {
      console.error('Error fetching production record:', error);
      toast.error('Chyba při načítání výrobního záznamu');
      navigate('/balirna');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleWeightChange = (index, value) => {
    const newWeights = [...formData.weight_checks];
    newWeights[index] = value;
    setFormData(prev => ({ ...prev, weight_checks: newWeights }));
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle photo from camera or file
  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Vyberte prosím obrázek');
      return;
    }

    // Compress image for mobile
    const reader = new FileReader();
    reader.onloadend = () => {
      // Create image to resize
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 1200;
        let width = img.width;
        let height = img.height;
        
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const compressedData = canvas.toDataURL('image/jpeg', 0.8);
        setFormData(prev => ({ ...prev, ink_jet_photo: compressedData }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setFormData(prev => ({ ...prev, ink_jet_photo: null }));
  };

  // Get planned quantity from production record
  const getPlannedQuantity = () => {
    // For existing records, production_data is flat
    // For new records, it's in productionRecord.data
    const data = productionRecord?.data || productionRecord || {};
    const planned = data['Množ.plán.'] || data['Skut. množství'] || data['MnožPlán'] || 0;
    return parseFloat(planned) || 0;
  };

  // Get unit from production record
  const getUnit = () => {
    const data = productionRecord?.data || productionRecord || {};
    return data['MJ'] || data.MJ || 'ks';
  };

  // Check if packaged quantity is less than planned
  const hasPackagingShortage = () => {
    const planned = getPlannedQuantity();
    const packaged = parseFloat(formData.packaged_quantity) || 0;
    return packaged > 0 && packaged < planned;
  };

  // Get the shortage difference
  const getShortageAmount = () => {
    const planned = getPlannedQuantity();
    const packaged = parseFloat(formData.packaged_quantity) || 0;
    return planned - packaged;
  };

  // Validate shortage data when there's a shortage
  const validateShortageData = () => {
    if (hasPackagingShortage()) {
      if (completionData.shortage_reasons.length === 0) {
        toast.error('Vyberte co nevyšlo (alespoň jednu položku)');
        return false;
      }
      if (completionData.shortage_reasons.includes('jine') && !completionData.shortage_reason_other.trim()) {
        toast.error('Zadejte co jiného nevyšlo');
        return false;
      }
      // Comment is required only when "Jiné" is selected
      if (completionData.shortage_reasons.includes('jine') && !completionData.shortage_comment.trim()) {
        toast.error('Komentář k položce "Jiné" je povinný');
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    // Validate packaged quantity is required
    if (!formData.packaged_quantity && formData.packaged_quantity !== 0) {
      toast.error('Zabalené množství je povinný údaj');
      return;
    }
    
    // Validate shortage data if there's a shortage
    if (!validateShortageData()) return;
    
    try {
      setSaving(true);
      
      const payload = {
        ...formData,
        production_record_id: productionId || productionRecord?.id,
        production_doklad: productionRecord?.doklad,
        production_data: productionRecord?.data || productionRecord,
        // Add completion data for shortage tracking
        completion_data: {
          planned_quantity: getPlannedQuantity(),
          actual_quantity: parseFloat(formData.packaged_quantity) || 0,
          shortage_reasons: hasPackagingShortage() ? completionData.shortage_reasons : [],
          shortage_reason_other: hasPackagingShortage() && completionData.shortage_reasons.includes('jine') ? completionData.shortage_reason_other : null,
          shortage_comment: hasPackagingShortage() ? completionData.shortage_comment : null
        }
      };
      
      if (isNew) {
        await api.post('/balirna/records', payload);
        toast.success('Kontrolní list uložen');
      } else {
        await api.put(`/balirna/records/${id}`, payload);
        toast.success('Kontrolní list aktualizován');
      }
      
      navigate('/balirna');
    } catch (error) {
      console.error('Error saving record:', error);
      toast.error('Chyba při ukládání');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    if (!id) {
      toast.error('Nejdříve uložte záznam');
      return;
    }
    
    try {
      const response = await api.get(`/balirna/records/${id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `balirna_${formData.batch_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Chyba při exportu PDF');
    }
  };

  // Large touch-friendly Yes/No toggle buttons
  const YesNoToggle = ({ label, field, value, subItem = false }) => (
    <div className={`flex items-center justify-between py-3 ${subItem ? 'pl-4 border-l-2 border-orange-200' : 'border-b'}`}>
      <span className={`${subItem ? 'text-sm' : 'text-base'} text-slate-700 flex-1 pr-4`}>{label}</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => handleCheckboxChange(field, true)}
          className={`min-w-[70px] h-12 rounded-lg font-medium text-base flex items-center justify-center gap-1 transition-all touch-manipulation ${
            value === true 
              ? 'bg-green-500 text-white shadow-md' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Check className="w-5 h-5" />
          Ano
        </button>
        <button
          type="button"
          onClick={() => handleCheckboxChange(field, false)}
          className={`min-w-[70px] h-12 rounded-lg font-medium text-base flex items-center justify-center gap-1 transition-all touch-manipulation ${
            value === false 
              ? 'bg-red-500 text-white shadow-md' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <X className="w-5 h-5" />
          Ne
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-4xl mx-auto pb-24">
        {/* Header - Sticky on tablet */}
        <div className="sticky top-0 z-20 bg-white -mx-4 md:-mx-6 px-4 md:px-6 py-3 border-b shadow-sm mb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/balirna')} className="h-12 w-12">
                <ArrowLeft className="w-6 h-6" />
              </Button>
              <div>
                <h1 className="text-lg md:text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Package className="w-5 h-5 md:w-6 md:h-6 text-orange-600" />
                  {isNew ? 'Nový kontrolní list' : 'Kontrolní list'}
                </h1>
                {productionRecord && (
                  <p className="text-xs md:text-sm text-slate-500 truncate max-w-[200px] md:max-w-none">
                    {formData.product_name}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              {!isNew && (
                <Button variant="outline" onClick={handleExportPDF} className="h-12 px-4">
                  <FileDown className="w-5 h-5 md:mr-2" />
                  <span className="hidden md:inline">PDF</span>
                </Button>
              )}
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-700 h-12 px-6"
              >
                <Save className="w-5 h-5 md:mr-2" />
                <span className="hidden md:inline">{saving ? 'Ukládám...' : 'Uložit'}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Kitchen Handover Info */}
          {kitchenHandover && (
            <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base text-orange-700">
                  <ChefHat className="w-5 h-5" />
                  Záznam o předávce z kuchyně
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-500">Předal:</span>
                    <span className="font-medium">{kitchenHandover.confirmed_by_name}</span>
                    <span className="text-slate-400">
                      ({kitchenHandover.confirmed_at ? new Date(kitchenHandover.confirmed_at).toLocaleString('cs-CZ') : '-'})
                    </span>
                  </div>
                  {kitchenHandover.balirna_verified && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="text-slate-500">Přijal:</span>
                      <span className="font-medium">{kitchenHandover.balirna_verified_by_name}</span>
                      <span className="text-slate-400">
                        ({kitchenHandover.balirna_verified_at ? new Date(kitchenHandover.balirna_verified_at).toLocaleString('cs-CZ') : '-'})
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Handover items */}
                <div className="bg-white rounded-lg border p-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-slate-600">Položka</th>
                        <th className="text-right py-2 text-slate-600">Předáno</th>
                        {kitchenHandover.balirna_corrections?.length > 0 && (
                          <th className="text-right py-2 text-slate-600">Přijato</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {kitchenHandover.items?.map((item, idx) => {
                        const correction = kitchenHandover.balirna_corrections?.find(c => c.item_id === item.item_id);
                        const hasDifference = correction && correction.received_quantity !== correction.original_quantity;
                        return (
                          <tr key={idx} className={`border-b last:border-0 ${hasDifference ? 'bg-yellow-50' : ''}`}>
                            <td className="py-2">{item.name}</td>
                            <td className="text-right font-medium">
                              {item.quantity} <span className="text-slate-500">{item.unit}</span>
                            </td>
                            {kitchenHandover.balirna_corrections?.length > 0 && (
                              <td className="text-right font-medium">
                                {correction ? (
                                  <span className={hasDifference ? 'text-orange-600' : ''}>
                                    {correction.received_quantity} <span className="text-slate-500">{item.unit}</span>
                                    {hasDifference && (
                                      <span className="text-xs text-orange-500 ml-1">
                                        ({correction.received_quantity > correction.original_quantity ? '+' : ''}{(correction.received_quantity - correction.original_quantity).toFixed(2)})
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-green-600">{item.quantity} <span className="text-slate-500">{item.unit}</span></span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Notes */}
                {(kitchenHandover.notes || kitchenHandover.balirna_notes) && (
                  <div className="mt-3 space-y-2 text-sm">
                    {kitchenHandover.notes && (
                      <div className="p-2 bg-orange-100 rounded">
                        <span className="text-orange-700 font-medium">Poznámka kuchyně: </span>
                        <span className="text-slate-700">{kitchenHandover.notes}</span>
                      </div>
                    )}
                    {kitchenHandover.balirna_notes && (
                      <div className="p-2 bg-blue-100 rounded">
                        <span className="text-blue-700 font-medium">Poznámka balírna: </span>
                        <span className="text-slate-700">{kitchenHandover.balirna_notes}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Basic Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-5 h-5 text-orange-600" />
                Základní údaje
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Datum</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="h-14 text-lg touch-manipulation"
                  />
                </div>
                
                <NumericInput
                  label="Teplota produktu (do 8°C)"
                  value={formData.product_temperature}
                  onChange={(v) => handleInputChange('product_temperature', v)}
                  placeholder="0.0"
                  unit="°C"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Čas začátku</Label>
                  <Input
                    type="time"
                    value={formData.packaging_start_time}
                    onChange={(e) => handleInputChange('packaging_start_time', e.target.value)}
                    className="h-14 text-lg touch-manipulation"
                  />
                </div>
                
                <div>
                  <Label className="text-sm text-slate-600">Čas konce</Label>
                  <Input
                    type="time"
                    value={formData.packaging_end_time}
                    onChange={(e) => handleInputChange('packaging_end_time', e.target.value)}
                    className="h-14 text-lg touch-manipulation"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-slate-600">Název produktu</Label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => handleInputChange('product_name', e.target.value)}
                  className="h-14 text-lg touch-manipulation"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Šarže</Label>
                  <Input
                    value={formData.batch_number}
                    onChange={(e) => handleInputChange('batch_number', e.target.value)}
                    className="h-14 text-lg touch-manipulation"
                  />
                </div>
                
                <NumericInput
                  label="Zabalené množství *"
                  value={formData.packaged_quantity}
                  onChange={(v) => handleInputChange('packaged_quantity', v)}
                  placeholder="0"
                  unit="ks"
                  required
                />
              </div>
              
              {/* Shortage Explanation - shows when packaged < planned */}
              {hasPackagingShortage() && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-700">
                      Méně zabaleno než plánováno (rozdíl: -{getShortageAmount().toLocaleString('cs-CZ')} {getUnit()})
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Co nevyšlo? * <span className="font-normal text-slate-500">(lze vybrat více)</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: 'omacka', label: 'Omáčka' },
                          { value: 'maso', label: 'Maso' },
                          { value: 'pevna_slozka', label: 'Pevná složka' },
                          { value: 'jine', label: 'Jiné...' }
                        ].map((option) => {
                          const isSelected = completionData.shortage_reasons.includes(option.value);
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setCompletionData(prev => ({
                                  ...prev,
                                  shortage_reasons: isSelected
                                    ? prev.shortage_reasons.filter(r => r !== option.value)
                                    : [...prev.shortage_reasons, option.value]
                                }));
                              }}
                              className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-2 ${
                                isSelected
                                  ? 'border-red-500 bg-red-100 text-red-700'
                                  : 'border-slate-200 bg-white hover:border-slate-300'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected ? 'border-red-500 bg-red-500' : 'border-slate-300'
                              }`}>
                                {isSelected && (
                                  <CheckCircle2 className="w-4 h-4 text-white" />
                                )}
                              </div>
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {completionData.shortage_reasons.includes('jine') && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Co jiného nevyšlo? *
                        </label>
                        <Input
                          value={completionData.shortage_reason_other}
                          onChange={(e) => setCompletionData(prev => ({
                            ...prev,
                            shortage_reason_other: e.target.value
                          }))}
                          placeholder="Zadejte důvod..."
                          className="bg-white"
                        />
                      </div>
                    )}
                    
                    {completionData.shortage_reasons.includes('jine') && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Komentář proč nevyšlo *
                        </label>
                        <textarea
                          value={completionData.shortage_comment}
                          onChange={(e) => setCompletionData(prev => ({
                            ...prev,
                            shortage_comment: e.target.value
                          }))}
                          rows={3}
                          className="w-full border rounded-lg p-3 text-sm bg-white"
                          placeholder="Popište důvod..."
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Weight Control */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Scale className="w-5 h-5 text-orange-600" />
                Kontrola hmotnosti
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <NumericInput
                  label="Váha obalu"
                  value={formData.packaging_weight}
                  onChange={(v) => handleInputChange('packaging_weight', v)}
                  placeholder="0"
                  unit="g"
                />
                <NumericInput
                  label="Deklarovaná hmotnost"
                  value={formData.declared_weight}
                  onChange={(v) => handleInputChange('declared_weight', v)}
                  placeholder="0"
                  unit="g"
                />
              </div>
              
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Váha produktu s obalem (10 měření)</Label>
                <div className="grid grid-cols-5 gap-2">
                  {formData.weight_checks.map((weight, index) => (
                    <div key={index}>
                      <span className="text-xs text-slate-400 block text-center mb-1">{index + 1}</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        value={weight}
                        onChange={(e) => handleWeightChange(index, e.target.value.replace(',', '.'))}
                        className="h-12 text-center text-lg touch-manipulation"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product Control */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <CheckCircle2 className="w-5 h-5 text-orange-600" />
                Kontrola produktu
              </CardTitle>
            </CardHeader>
            <CardContent>
              <YesNoToggle 
                label="Správnost obalu" 
                field="packaging_correct" 
                value={formData.packaging_correct} 
              />
              
              <YesNoToggle 
                label="Správnost sváru" 
                field="seam_correct" 
                value={formData.seam_correct} 
              />
              
              {/* Ink jet print control section */}
              <div className="py-3 border-b">
                <p className="text-base font-medium text-slate-800 mb-2">
                  Správnost, čitelnost a nesmazatelnost tisku (ink jet):
                </p>
                <div className="space-y-2">
                  <YesNoToggle 
                    label="První výrobek" 
                    field="first_product_ok" 
                    value={formData.first_product_ok}
                    subItem 
                  />
                  <YesNoToggle 
                    label="Poslední výrobek" 
                    field="last_product_ok" 
                    value={formData.last_product_ok}
                    subItem 
                  />
                </div>
              </div>
              
              <YesNoToggle 
                label="Provedena přechodová sanitace po alergenu" 
                field="allergen_sanitation" 
                value={formData.allergen_sanitation} 
              />
              <YesNoToggle 
                label="Prochází produkt metal detektorem" 
                field="passes_metal_detector" 
                value={formData.passes_metal_detector} 
              />
            </CardContent>
          </Card>

          {/* Metal Detector */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                Kontrola metal detektoru
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Start of batch section */}
              <div className="mb-4">
                <p className="text-base font-medium text-slate-800 mb-2 pb-2 border-b">
                  Produkt vyřazen na začátku šarže:
                </p>
                <div className="space-y-2">
                  <YesNoToggle label="Etalon nerez: 2,5 mm" field="etalon_stainless_start" value={formData.etalon_stainless_start} subItem />
                  <YesNoToggle label="Etalon železo: 2,5 mm" field="etalon_iron_start" value={formData.etalon_iron_start} subItem />
                  <YesNoToggle label="Etalon neželezo: 2,5 mm" field="etalon_nonferrous_start" value={formData.etalon_nonferrous_start} subItem />
                </div>
              </div>
              
              {/* End of batch section */}
              <div>
                <p className="text-base font-medium text-slate-800 mb-2 pb-2 border-b">
                  Produkt vyřazen na konci šarže:
                </p>
                <div className="space-y-2">
                  <YesNoToggle label="Etalon nerez: 2,5 mm" field="etalon_stainless_end" value={formData.etalon_stainless_end} subItem />
                  <YesNoToggle label="Etalon železo: 2,5 mm" field="etalon_iron_end" value={formData.etalon_iron_end} subItem />
                  <YesNoToggle label="Etalon neželezo: 2,5 mm" field="etalon_nonferrous_end" value={formData.etalon_nonferrous_end} subItem />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Signature & Photo */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Camera className="w-5 h-5 text-orange-600" />
                Podpis a foto tisku
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Zapsal</Label>
                <Input
                  value={formData.filled_by}
                  onChange={(e) => handleInputChange('filled_by', e.target.value)}
                  placeholder="Jméno pracovníka"
                  className="h-14 text-lg touch-manipulation"
                />
              </div>
              
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Foto injektovaného tisku</Label>
                
                {formData.ink_jet_photo ? (
                  <div className="relative">
                    <img 
                      src={formData.ink_jet_photo} 
                      alt="Foto tisku" 
                      className="w-full max-w-md rounded-lg border"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 h-12 w-12"
                      onClick={removePhoto}
                    >
                      <Trash2 className="w-6 h-6" />
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {/* Camera button */}
                    <button
                      type="button"
                      onClick={() => cameraInputRef.current?.click()}
                      className="border-2 border-dashed border-orange-300 rounded-lg p-6 text-center hover:bg-orange-50 transition-colors touch-manipulation"
                    >
                      <Camera className="w-10 h-10 text-orange-500 mx-auto mb-2" />
                      <p className="text-orange-600 font-medium">Vyfotit</p>
                    </button>
                    
                    {/* Gallery button */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:bg-slate-50 transition-colors touch-manipulation"
                    >
                      <FileDown className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-600 font-medium">Z galerie</p>
                    </button>
                  </div>
                )}
                
                {/* Hidden file inputs */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handlePhotoCapture}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoCapture}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Floating save button for mobile */}
        <div className="fixed bottom-4 left-4 right-4 md:hidden">
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-orange-600 hover:bg-orange-700 h-14 text-lg shadow-lg"
          >
            <Save className="w-6 h-6 mr-2" />
            {saving ? 'Ukládám...' : 'Uložit kontrolní list'}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default BalirnaDetailPage;
